import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSweepNotifications } from '../components/notifications/useSweepNotifications';
import { reportsData } from '../components/pages/reports/data/reports';
import { ReportingStatus } from '../components/pages/reports/ReportActivationStatuses';
import { SweepNotificationVariant } from '../reducers/notificationsReducer';
import {
  setIsAllowedToActivateReporting,
  setReportsList,
  updateReport,
} from '../reducers/reportsReducer';
import { Report } from '../types/Reports';
import { telemetry } from '../telemetry';
import {
  SfdcReportAttributes,
  SfdcReportColumnsAttributes,
} from '../components/Automations/SalesforceReportSelector';
import { isCustomObjectByName } from '../components/documentation/cpq/utils';
import { useReportingApiFacade } from '../apis/facades/useReportingApiFacade';

enum ActionType {
  ACTIVATION = 'activation',
  DEACTIVATION = 'deactivation',
}

export const useReports = () => {
  const dispatch = useDispatch();
  const { addNotification } = useSweepNotifications();
  const {
    get_reports,
    get_isAllowedToActivateReporting,
    patch_activateReport,
    post_deactivateReport,
    get_tabularReports,
    get_reportColumnsIds,
  } = useReportingApiFacade();

  const getReportsDetails = async () => {
    try {
      const response = await get_reports();
      const mergedReportData = response?.map((report: Report) => {
        const localReport = reportsData.find((localReport) => localReport.type === report.type);
        return { ...report, ...localReport };
      });

      dispatch(setReportsList({ reports: mergedReportData }));
    } catch (e) {
      telemetry.captureError(e);
    }
  };

  const activateReport = async (report: Report) => {
    try {
      dispatch(updateReport({ newReport: { ...report, status: ReportingStatus.PENDING } })); //update status without waiting for response
      const response = await patch_activateReport(report);
      dispatch(updateReport({ newReport: response }));
    } catch (e) {
      telemetry.captureError(e);
      displayNotification('error', ActionType.ACTIVATION, report);
      dispatch(updateReport({ newReport: { ...report, status: ReportingStatus.INACTIVE } }));
    }
  };

  const displayNotification = useCallback(
    (type: string, actionType: ActionType, report: Report) => {
      let message;
      const isError = type === 'error';
      const variant = isError ? SweepNotificationVariant.Error : SweepNotificationVariant.Default;

      switch (actionType) {
        case ActionType.ACTIVATION: //Only if something goes wrong outside websocket
          message = isError ? `Activation failed` : '';
          break;

        case ActionType.DEACTIVATION:
          message = isError ? `Deactivation failed` : `“${report.name}” is deactivated now`;
          break;
      }

      message &&
        addNotification({
          message,
          variant,
        });
    },
    [addNotification],
  );

  const deactivateReport = async (report: Report) => {
    try {
      const response = await post_deactivateReport(report.id ?? '');
      dispatch(updateReport({ newReport: response }));
      displayNotification('success', ActionType.DEACTIVATION, report);
    } catch (e) {
      telemetry.captureError(e);
      displayNotification('error', ActionType.DEACTIVATION, report);
    }
  };

  const getSfdcTabularReports = async () => {
    try {
      const response = await get_tabularReports();
      return response as SfdcReportAttributes[];
    } catch (e) {
      telemetry.captureError(e);
    }
  };

  const getSfdcReportColumnsIds = async (reportId: string) => {
    try {
      const response = await get_reportColumnsIds(reportId);
      const types = response as SfdcReportColumnsAttributes[];
      const idTypes = types.filter((el: SfdcReportColumnsAttributes) => {
        // for standard fields: only when dataType = id, for custom fields (__c) - only when isLookup = true
        const isCustom = isCustomObjectByName(el?.fieldApiName ?? '');
        if (isCustom) {
          return el.isLookup;
        } else {
          return el.dataType === 'id';
        }
      });
      return idTypes;
    } catch (e) {
      telemetry.captureError(e);
    }
  };

  const getIsAllowedToActivateReporting = useCallback(async () => {
    try {
      const response = await get_isAllowedToActivateReporting();
      dispatch(setIsAllowedToActivateReporting(response ?? false));
    } catch (e) {
      telemetry.captureError(e);
    }
  }, [dispatch, get_isAllowedToActivateReporting]);

  return {
    getReportsDetails,
    deactivateReport,
    activateReport,
    getSfdcTabularReports,
    getSfdcReportColumnsIds,
    getIsAllowedToActivateReporting,
  };
};
